import {range} from '@benchling/js-utils';
import produce from 'immer';
import {useCallback} from 'react';

import {assert} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import checkSoftInvariant from 'lib/checkSoftInvariant';
import DocumentStore from 'notebook/documents/DocumentStore';
import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import {resolveSelectorsFromPastedHTMLTables} from 'notebook/plain-tables/selectors/SelectorConflictResolver';
import {getTableLengthErrorMessage} from 'notebook/structured-tables/util/checkTableCapacity';
import type {InitialCellValue} from 'notebook/structured-tables/util/getInitialCellValueFromLiveValue';
import type {Cell, PastedCell} from 'notebook/tables/constants/CellConstants';
import {type InsertableColumnInfo} from 'notebook/tables/constants/TableColumnConstants';
import * as TableConstants from 'notebook/tables/constants/TableConstants';
import {
  MultiSelectionBox,
  TableDimension,
  TableIndex,
} from 'notebook/tables/constants/TableSelectionConstants';
import {computeFrozenCountAfterReorder} from 'notebook/tables/core-ui/FreezeColumnUtils';
import {type FullTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {type TableOperation} from 'notebook/tables/excess/ops/TableOpConstants';
import {ReorderManualOptions, ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import {colNumberToLetters, rowIndexToNumber} from 'notebook/tables/excess/Util';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import {useTableOps} from 'notebook/tables/spreadsheets/TableOpContext';
import {TableModelContext} from 'notebook/tables/store/contexts/TableModelContext';
import {
  type TableNoteContextInterface,
  useNullableTableNoteContext,
} from 'notebook/tables/store/contexts/TableNoteContext';
import {useTableModel} from 'notebook/tables/store/hooks/useTableModel';
import {clearCellsInRange as clearCellsInRangeMutation} from 'notebook/tables/store/logic/mutations/clearCellsInRange';
import {
  fillDown as fillDownMutation,
  doubleClickFillDown as doubleClickFillDownMutation,
} from 'notebook/tables/store/logic/mutations/fillDown';
import {
  pasteTable as pasteTableMutation,
  preparePastedCells,
  processPastedTypedValues,
} from 'notebook/tables/store/logic/mutations/pasteTable';
import {
  toggleHeaderCol as toggleHeaderColMutation,
  toggleHeaderRow as toggleHeaderRowMutation,
} from 'notebook/tables/store/logic/mutations/toggleHeader';
import {updateCell as updateCellMutation} from 'notebook/tables/store/logic/mutations/updateCell';
import {computeAnyCellsLockedOrArchived} from 'notebook/tables/store/logic/TableLockingLogic';
import {
  computeMergeValidation,
  findTablesReferencingMergedChildCells,
  type MergeValidation,
} from 'notebook/tables/store/logic/TableMergingLogic';
import {
  computeSelectionRestoreMetadata,
  computeMultiIndicesState,
  getMultiEditingText,
  getMultiEditingTypedValue,
} from 'notebook/tables/store/logic/TableMultiSelectionLogic';
import {computePasteRejectionReason} from 'notebook/tables/store/logic/TablePasteLogic';
import {computeExpandedSelectionBox} from 'notebook/tables/store/logic/TableSelectionLogic';
import {getTableMapFromNote} from 'notebook/tables/store/selectors/TableSelectors';
import TableUtil from 'notebook/tables/TableUtil';
import {
  trackCrossStepReferenceChange,
  trackCrossStepReferenceBulkDelete,
} from 'notebook/tables/util/analytics/CrossStepReferenceAnalytics';
import {getWorksheetIdFromNamespace} from 'notebook/tables/util/WorksheetTableNamespaceUtil';
import {trackDragToReorderRowsOrColumns} from 'notebook/util/analytics/TableAnalyticsUtils';
import notifications from 'notifications';
import {trackContainerValidityForTableEvents} from 'samples/containers/ValidContentsUtils';

interface TableOpsMethods {
  renameTable: (tableName: string, newTableName: string) => void;
  performTableOp?: (tableOp: TableOperation) => void;
  deleteColumns: (tableName: string, start: number, end: number) => void;
  insertColumnsLeft: (
    tableName: string,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  insertColumnsRight: (
    tableName: string,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  deleteRowsNonContiguous: (tableName: string, ranges: Array<[number, number]>) => void;
  mergeRegion: ((tableName: string, selectedBox: MultiSelectionBox) => void) | null;
  unmergeRegion: ((tableName: string, selectedBox: MultiSelectionBox) => void) | null;
  /**
   * Reorder rows/columns of the table via the underlying table op. This is the raw
   * op used to reorder single-input columns; drag-to-reorder of regular rows/columns
   * goes through {@link TableMutationMethods.reorderRowsOrColumns}.
   */
  reorderSingleInputs: (tableName: string, options: ReorderOptions) => void;
}

export interface TableMutationMethods extends TableOpsMethods {
  /**
   * Update a single cell in the table.
   *
   * @param cellIndex - The [row, column] index of the cell to update
   * @param cellData - Partial cell data to merge into the existing cell
   * @param expandTable - If true, add a new blank row at the bottom
   */
  updateCell: (cellIndex: TableIndex, cellData: Partial<Cell>, expandTable?: boolean) => void;

  /**
   * Clear all cells within a selection box.
   *
   * @param selectionBox - The box defining which cells to clear
   */
  clearCellsInRange: (selectionBox: MultiSelectionBox) => void;

  /**
   * Clear all cells within the current selection, expanding to fully contain any
   * intersecting merged regions.
   *
   * Checkpoints the current selection for undo, clears the cells, and leaves cell
   * editing. No-op when there is no real selection.
   */
  clearSelection: () => void;

  /**
   * Delete a contiguous range of rows.
   *
   * Checkpoints the deleted rows as the selection for undo, then deletes them. `end`
   * defaults to `start`; no-op when `start` is null.
   */
  deleteRows: (start: number | null, end: number | null) => void;

  /**
   * Insert as many rows above the given range as the range spans.
   *
   * Checkpoints the range as the selection for undo before inserting. `end` defaults
   * to `start`; no-op when `start` is null.
   */
  insertRowsAbove: (start: number | null, end: number | null) => void;

  /**
   * Insert as many rows below the given range as the range spans.
   *
   * Checkpoints the range as the selection for undo before inserting. `end` defaults
   * to `start`; no-op when `start` is null.
   */
  insertRowsBelow: (start: number | null, end: number | null) => void;

  /**
   * Delete the whole table via the embedding surface's delete handler.
   *
   * Only valid when {@link TableModel.canDeleteTable} is true; callers gate on it.
   */
  deleteTable: () => void;

  /**
   * Toggle the header column flag on the table, clearing bold formatting on the first column.
   */
  toggleHeaderCol: () => void;

  /**
   * Toggle the header row flag on the table, clearing bold formatting on the first row.
   */
  toggleHeaderRow: () => void;

  /**
   * Validate whether the current selection can be merged into a single cell.
   *
   * Reads the merge-expanded selection box from the model and reports whether the
   * selection straddles the freeze boundary (merging disallowed) and whether the
   * merge would discard cell values (caller should confirm). Returns `null` when
   * there is no active selection.
   *
   * The merge itself is performed separately via `mergeRegion`; the confirmation
   * modal is owned by the component.
   */
  validateMerge: () => MergeValidationResult | null;

  /**
   * Paste a 2D array of cells into the table.
   *
   * Orchestrates the full paste pipeline (excluding clipboard parsing and file
   * attachment, which remain in the component): resolves the destination
   * selection, tiles/reshapes the pasted cells to fill it, validates the paste
   * (surfacing warnings/errors via notifications), normalizes typed values,
   * asynchronously resolves selectors, writes the cells via the pure
   * `pasteTable` mutation, enforces capacity limits, remerges single-input
   * columns, persists the change, and tracks analytics.
   *
   * @param cells - The 2D array of cells to paste (already parsed from the clipboard).
   * @param options - Paste options: `transpose` and the originating `selectionIndex`.
   * @param setSelection - Component callback to update the multi-selection. Called
   *   once with the expanded paste region (so a rejected paste is visible) and again
   *   with the final pasted region.
   */
  pasteTable: (
    cells: Array<Array<PastedCell>>,
    options: PasteTableOptions,
    setSelection: (args: {start: TableIndex; end: TableIndex; text?: string}) => void
  ) => void;

  /**
   * Set the table's multi-selection anchor (`start`) and end indices.
   *
   * Writes the selection indices to the store and syncs the spreadsheet editing
   * state for the anchor cell. When `text` is omitted, the anchor cell's current
   * text / typed value are read from the table.
   *
   * Does NOT move DOM focus — the caller is responsible for focusing the
   * focus-bearer element afterward when needed.
   *
   * @param indices - The `start` (anchor) and `end` indices, plus optional `text`
   *   to seed the editing state for the anchor cell
   * @param options - Optional `multiSelIndex` to keep as the selected index;
   *   defaults to the `start` anchor when omitted
   */
  setMultiIndices: (
    indices: {start: TableIndex; end: TableIndex; text?: string},
    options?: {multiSelIndex?: TableIndex | null}
  ) => void;

  /**
   * Checkpoint the current selection onto the undo/redo restore history.
   *
   * Snapshots the current multi-selection state (or an explicit override) and
   * pushes it via the table's `storeState` handler. Typically called before a
   * structural edit so the prior selection can be restored on undo.
   *
   * @param override - Optional selection state to snapshot instead of the
   *   current store state
   */
  saveSelection: (
    override?: Partial<MultiSelectListUtil.MultiSelectState<TableIndex>> | {selection: SelectionRestoreData}
  ) => void;

  /**
   * Complete a drag-to-reorder of rows or columns.
   *
   * Encapsulates the full `onDragComplete` flow: reads the in-progress drag
   * dimension and multi-selection from the model, clears the dragging state,
   * rejects (surfacing an error notification) when the drop position would split
   * a merged cell, checkpoints the selection for undo via `saveSelection`,
   * adjusts the frozen-column count for column reorders, performs the reorder via
   * the `reorderSingleInputs` op (skipped when the drop is a no-op), updates the
   * selection to the reordered range via `setMultiIndices`, and tracks analytics.
   *
   * Does NOT move DOM focus — the caller is responsible for focusing the
   * focus-bearer element afterward.
   *
   * @param insertIndex - The index the dragged range is dropped before.
   */
  reorderRowsOrColumns: (insertIndex: number) => void;

  /**
   * Fill down the current selection into the active fill-down box.
   *
   * Checks fill-down locking (surfacing a warning notification and aborting when
   * locked), reads the source selection (`expandedBox`) and destination
   * (`fillDownBox`) from the models, checkpoints the current selection for undo
   * via `saveSelection`, extrapolates via the pure `fillDown` mutation,
   * persists the change, and updates the selection to the filled region via
   * `setMultiIndices`. No-op when there is no active selection or fill-down box.
   */
  performFillDown: () => void;

  /**
   * Fill down the current selection through the following blank rows, triggered
   * by double-clicking the fill handle.
   *
   * Reads the source selection (`expandedBox`) and the double-click destination
   * (`fillDown.doubleClickBox`) from the models, extrapolates via the pure
   * `doubleClickFillDown` mutation, persists the change, and updates the
   * selection to the filled region via `setMultiIndices`. No-op when the
   * selection contains a merged region.
   */
  performDoubleClickFillDown: () => void;
}

/** Options for {@link TableMutationMethods.pasteTable}. */
export interface PasteTableOptions {
  /** Whether to transpose the pasted data before pasting. */
  transpose?: boolean;
  /**
   * The cell index that originated the paste. Used for async pastes (e.g. context
   * menu) that may resolve after the active selection has changed.
   */
  selectionIndex?: TableIndex | null;
}

/**
 * The result of validating a merge of the current selection, including the
 * merge-expanded box that should be passed to `mergeRegion`.
 */
export interface MergeValidationResult extends MergeValidation {
  /** The merge-expanded selection box to merge. */
  box: MultiSelectionBox;

  /**
   * Names of tables in the entry that contain formulas referencing subordinate (child) cells of the
   * merged region. Merging empties those cells, so such references may become incorrect; the caller
   * should warn the user. Empty when no such references exist.
   */
  tablesReferencingMergedChildCells: Array<string>;
}

/**
 * Builds the map of all tables in the entry/document, keyed by name, so merge validation can scan every
 * table's formulas for references into the region being merged.
 *
 * The full note is read from {@link TableNoteContext} (`getNote`), which resolves to the live note for
 * whatever surface is mounted — regular ELN entries (backed by `EntryStore`), worksheets/documents
 * (`DocumentStore`), templates, etc. Falls back to just the table being edited when no note context is
 * available (e.g. isolated tests or harnesses). The edited table is always overlaid from the live model so
 * its formulas reflect unsaved edits.
 */
function getEntryTableMap(
  tableNoteContext: TableNoteContextInterface | null,
  currentTable: TableConstants.Table
): FullTableMap {
  const currentTableMap: FullTableMap = {[currentTable.name]: currentTable};
  if (tableNoteContext == null) {
    return currentTableMap;
  }
  const note = tableNoteContext.getNote();
  return {...getTableMapFromNote(note), ...currentTableMap};
}

/**
 * Hook that provides table mutation methods with analytics and persistence.
 *
 * All data is fetched fresh from the TableModel at execution time,
 * ensuring we always use the latest state and keeping callbacks stable.
 */
export function useTableMutations(): TableMutationMethods {
  const {useTableEditableStore, tableModelSelector, setState} = TableModelContext.useContext();
  const tableOps = useTableOps();
  const {tableModel: disableMergingCells} = useTableModel((state) => state.props.disableMergingCells);
  // Nullable so merge validation still works in isolated contexts (tests/harnesses) that do not provide a
  // note context; in that case only the edited table is scanned for subordinate-cell references.
  const tableNoteContext = useNullableTableNoteContext();

  const getTableModel = useCallback(
    () => tableModelSelector(useTableEditableStore.getState()),
    [useTableEditableStore, tableModelSelector]
  );

  const updateCell = useCallback(
    (cellIndex: TableIndex, cellData: Partial<Cell>, expandTable?: boolean): void => {
      const model = getTableModel();
      const {table, index, fileId, tableNamespace, updateHandler} = model.props;
      const {boundaryIndex: singleInputBoundaryIndex} = model.singleInputs;

      // Execute pure mutation
      const result = updateCellMutation(table, cellIndex, cellData, {
        expandTable,
        singleInputBoundaryIndex,
      });

      // Track cross-step reference analytics
      if (cellData.text != null) {
        const oldCellText = result.affectedCells[0]?.oldCell?.text ?? '';
        const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
        const worksheetInfo = worksheetId
          ? {worksheetId, stepCount: DocumentStore.getRelatedDocumentIds(fileId).length}
          : null;
        trackCrossStepReferenceChange(oldCellText, cellData.text, worksheetInfo);
      }

      // Persist the change
      updateHandler(
        index,
        {table: result.table},
        undefined,
        result.tableOp != null ? {[table.name]: result.tableOp} : undefined
      );

      // Track container validity analytics
      const newCell = result.affectedCells[0]?.newCell;
      if (newCell) {
        trackContainerValidityForTableEvents(fileId, table.apiId!, [
          {event: ELN_ANALYTICS_ACTIONS.ADDED_CONTAINERS_TO_STRUCTURED_TABLE, cells: [newCell]},
        ]);
      }
    },
    [getTableModel]
  );

  const clearCellsInRange = useCallback(
    (selectionBox: MultiSelectionBox): void => {
      const model = getTableModel();
      const {table, index, fileId, tableNamespace, updateHandler} = model.props;

      // Execute pure mutation
      const result = clearCellsInRangeMutation(table, selectionBox);

      // Track cross-step reference analytics
      const oldCells = result.affectedCells.map((c) => c.oldCell).filter((c): c is Cell => c != null);
      const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
      const worksheetInfo = worksheetId
        ? {worksheetId, stepCount: DocumentStore.getRelatedDocumentIds(fileId).length}
        : null;
      trackCrossStepReferenceBulkDelete(oldCells, worksheetInfo);

      // Persist the change
      updateHandler(
        index,
        {table: result.table},
        undefined,
        result.tableOp != null ? {[table.name]: result.tableOp} : undefined
      );

      // Track container validity analytics
      if (oldCells.length > 0) {
        trackContainerValidityForTableEvents(fileId, table.apiId!, [
          {
            event: ELN_ANALYTICS_ACTIONS.REMOVED_CONTAINERS_FROM_STRUCTURED_TABLE,
            cells: oldCells,
          },
        ]);
      }
    },
    [getTableModel]
  );

  const saveSelection = useCallback(
    (
      override?: Partial<MultiSelectListUtil.MultiSelectState<TableIndex>> | {selection: SelectionRestoreData}
    ): void => {
      const model = getTableModel();
      const stateToSave = override != null ? override : model.state;
      const metadata = computeSelectionRestoreMetadata(stateToSave, model.selection.multiSelectKey);
      model.props.storeState(metadata);
    },
    [getTableModel]
  );

  const clearSelection = useCallback((): void => {
    const model = getTableModel();
    const {hasRealSelection, expandedBox} = model.selection;

    // Nothing to clear when the selection does not cover any real cells.
    if (!hasRealSelection) {
      return;
    }

    saveSelection();

    // `hasRealSelection` implies a selection box, but the box is still read defensively
    // so a missing one skips the cell writes rather than throwing.
    if (expandedBox != null) {
      clearCellsInRange(expandedBox);
    }

    const {table, tableNamespace, fileId, documentName} = model.props;
    SpreadsheetEvalUtil.setMultiEditingForTable(
      {tableName: table.name, documentName: documentName ?? null},
      tableNamespace,
      fileId,
      {editing: false, index: null, text: '', typedValue: null, selection: null}
    );
  }, [getTableModel, saveSelection, clearCellsInRange]);

  const toggleHeaderCol = useCallback((): void => {
    const model = getTableModel();
    const {table, index, updateHandler} = model.props;

    const result = toggleHeaderColMutation(table);

    updateHandler(
      index,
      {table: result.table},
      undefined,
      result.tableOp != null ? {[table.name]: result.tableOp} : undefined
    );
  }, [getTableModel]);

  const toggleHeaderRow = useCallback((): void => {
    const model = getTableModel();
    const {table, index, updateHandler} = model.props;

    const result = toggleHeaderRowMutation(table);

    updateHandler(
      index,
      {table: result.table},
      undefined,
      result.tableOp != null ? {[table.name]: result.tableOp} : undefined
    );
  }, [getTableModel]);

  const validateMerge = useCallback((): MergeValidationResult | null => {
    const model = getTableModel();
    const {expandedBox} = model.selection;

    // Nothing to validate when there is no active selection.
    if (expandedBox == null) {
      return null;
    }

    const {table} = model.props;
    const freezeBoundary = TableUtil.getFreezeBoundary(table);
    const validation = computeMergeValidation(table.table, expandedBox, freezeBoundary);

    const tablesReferencingMergedChildCells = findTablesReferencingMergedChildCells(
      getEntryTableMap(tableNoteContext, table),
      table.name,
      expandedBox
    );

    return {box: expandedBox, ...validation, tablesReferencingMergedChildCells};
  }, [getTableModel, tableNoteContext]);

  const pasteTable = useCallback(
    (
      cells: Array<Array<PastedCell>>,
      options: PasteTableOptions,
      setSelection: (args: {start: TableIndex; end: TableIndex; text?: string}) => void
    ): void => {
      const model = getTableModel();
      const {
        table,
        computedTable,
        preserveMergedCellsInPaste,
        allowMergeSelection,
        selectorsLink,
        selectorsEnabled,
      } = model.props;
      const {multiStartIndex, multiEndIndex} = model.state;
      const {selectionIndex} = options;

      // Async paste (e.g. context menu) can resolve after the user clicks elsewhere,
      // so the current multi-selection may no longer match the cell that initiated the
      // paste. options.selectionIndex captures that originating cell. If it still falls
      // inside the active multi-selection, keep the full range so tile-fill works;
      // otherwise paste into just the originating cell.
      let selectionStart: TableIndex;
      let selectionEnd: TableIndex;
      if (
        selectionIndex != null &&
        multiStartIndex != null &&
        multiEndIndex != null &&
        TableUtil.isIndexBetween(multiStartIndex, multiEndIndex, selectionIndex)
      ) {
        selectionStart = multiStartIndex;
        selectionEnd = multiEndIndex;
      } else if (selectionIndex != null) {
        selectionStart = selectionIndex;
        selectionEnd = selectionIndex;
      } else if (multiStartIndex != null && multiEndIndex != null) {
        selectionStart = multiStartIndex;
        selectionEnd = multiEndIndex;
      } else {
        checkSoftInvariant(false, {
          message: 'Unable to paste: no selection or originating cell index available',
          expirationDate: '2027-04-01',
          errorInDevAndTest: true,
        });
        return;
      }

      const box = computeExpandedSelectionBox(selectionStart, selectionEnd, table, computedTable);
      // Matches the original `TableEditable._multiSelectionBox`, which hard-asserts a
      // non-null box in all environments (including production).
      assert(box != null, 'Expected to have a selection');

      // 1. Tile / reshape the pasted cells so they fill the selection.
      const preparedCells = preparePastedCells(table, computedTable, cells, box, {
        transpose: options.transpose,
        preserveMergedCellsInPaste: Boolean(preserveMergedCellsInPaste),
        disallowCustomColumns: Boolean(table.disallowCustomColumns),
      });

      // Set the selection according to the expanded paste table so that if it fails the user can see the issue
      setSelection({
        start: [box.minI, box.minJ],
        end: [
          Math.max(box.maxI, box.minI + preparedCells.length - 1),
          Math.max(box.maxJ, box.minJ + preparedCells[0].length - 1),
        ],
      });

      // 2. Validate the paste before mutating.
      const pasteRejection = computePasteRejectionReason(
        table,
        computedTable,
        preparedCells,
        box.minI,
        box.minJ,
        {
          supportsMerge: model.merging.supportsMerge,
          preserveMergedCellsInPaste: Boolean(preserveMergedCellsInPaste),
          allowMergeSelection: allowMergeSelection ?? undefined,
          isAnyCellLockedOrArchived: (lockCells) => computeAnyCellsLockedOrArchived(lockCells, table.rowData),
        }
      );
      if (pasteRejection != null) {
        if (pasteRejection.severity === 'warn') {
          notifications.warn(pasteRejection.reason);
        } else {
          notifications.error(pasteRejection.reason);
        }
        return;
      }

      // 3. Normalize typed values.
      const processedCells = processPastedTypedValues(table, preparedCells);

      // 4. Resolve selectors, then paste the tiled table at the top-left cell of the selection.
      resolveSelectorsFromPastedHTMLTables({
        pastedTables: [processedCells],
        existingSelectors: selectorsLink.value,
        // `selectorsEnabled` is optional and defaults to enabled, matching the
        // default applied to the `TableEditable` component props.
        selectorsEnabled: selectorsEnabled ?? true,
      }).then((selectors) => {
        // Selector resolution is async, so the table may have changed since the paste
        // was initiated. Re-read the latest model here so we paste against the current
        // table / computedTable / handlers, matching the original `_pasteTable` which
        // read `this.props.*` live inside the resolution callback. The prepared cells
        // and top-left paste coordinate (box.minI / box.minJ) are intentionally kept
        // from the initiation-time snapshot, exactly as the original did.
        const latestModel = getTableModel();
        const {
          table: latestTable,
          index: latestIndex,
          fileId: latestFileId,
          computedTable: latestComputedTable,
          preserveMergedCellsInPaste: latestPreserveMergedCellsInPaste,
          updateHandler: latestUpdateHandler,
        } = latestModel.props;

        const pasteResult = pasteTableMutation(
          latestTable,
          latestComputedTable,
          processedCells,
          box.minI,
          box.minJ,
          Boolean(latestPreserveMergedCellsInPaste)
        );
        const {box: pasteBox, tableOp} = pasteResult;
        let {table: newTable} = pasteResult;

        const tableMetadata = latestTable.metadata
          ? (latestTable.metadata as TableConstants.StructuredTableToTableMetadata<unknown>)
          : undefined;

        // If tableMetadata is null, do not check for capacity here - it may be a plain table. Structured tables
        // will still catch the capacity issue before making changes, via a check in StructuredTablePlugin.setContent.
        const capacityErrorMsg =
          tableMetadata != null
            ? getTableLengthErrorMessage({
                currentTableLength: latestTable.table.length,
                newTableLength: newTable.table.length,
                structuredTableKind: tableMetadata.kind,
                isTableEditable: true,
                allowAddRows: latestModel.dimensions.canAddRows,
                allowRemoveRows: latestModel.dimensions.canRemoveRows,
              })
            : null;
        if (capacityErrorMsg) {
          notifications.error(capacityErrorMsg);
          return;
        }

        // TODO(T7414): Refactor into `excess/ops/TableOps`
        if (TableUtil.isInvalidOpWithWarn(latestTable, newTable)) {
          return;
        }

        // If any single inputs are present and the table is expanding, merge the single input columns accordingly.
        if (latestModel.singleInputs.boundaryIndex && newTable.table.length > latestTable.table.length) {
          // TODO(BNCH-136509): Possibly update tableOp to include single input merged cells?
          newTable = TableUtil.remergeSingleInputColumns(newTable);
        }

        // Update state (table and selectors) / selection
        const replacedCells = latestTable.table
          .slice(pasteBox.minI, pasteBox.maxI + 1)
          .flatMap((row) => row.slice(pasteBox.minJ, pasteBox.maxJ + 1));
        setSelection({
          start: [pasteBox.minI, pasteBox.minJ],
          end: [pasteBox.maxI, pasteBox.maxJ],
          text: newTable.table[pasteBox.minI][pasteBox.minJ].text,
        });
        latestUpdateHandler(latestIndex, {table: newTable}, selectors, {[newTable.name]: tableOp});

        // Track container validity analytics
        trackContainerValidityForTableEvents(latestFileId, latestTable.apiId!, [
          {
            event: ELN_ANALYTICS_ACTIONS.ADDED_CONTAINERS_TO_STRUCTURED_TABLE,
            cells: processedCells.flat(),
          },
          {event: ELN_ANALYTICS_ACTIONS.REMOVED_CONTAINERS_FROM_STRUCTURED_TABLE, cells: replacedCells},
        ]);
      });
    },
    [getTableModel]
  );

  const setMultiIndices = useCallback(
    (
      {start, end, text}: {start: TableIndex; end: TableIndex; text?: string},
      options: {multiSelIndex?: TableIndex | null} = {}
    ): void => {
      const model = getTableModel();
      const {table, tableNamespace, fileId, documentName} = model.props;

      // The selected index defaults to `start` when no explicit `multiSelIndex` is provided.
      setState(computeMultiIndicesState({start, end}, options.multiSelIndex ?? null));

      const editingText = text != null ? text : getMultiEditingText(table, start);
      const typedValue = getMultiEditingTypedValue(table, start);
      SpreadsheetEvalUtil.setMultiEditingForTable(
        {tableName: table.name, documentName: documentName ?? null},
        tableNamespace,
        fileId,
        {editing: false, index: start, text: editingText, typedValue, selection: null}
      );
    },
    [getTableModel, setState]
  );

  /**
   * Checkpoints a row range as the selection for undo before a structural row edit. The
   * range spans the visible (effective) columns, matching the geometry the row axis cell
   * selects.
   */
  const saveRowRangeSelection = useCallback(
    (start: number, end: number): void => {
      const model = getTableModel();
      const {start: sliceStart, end: sliceEnd} = model.singleInputs.effectiveSlices;
      const startIndex: TableIndex = [Math.min(start, end), sliceStart];
      const endIndex: TableIndex = [Math.max(start, end), sliceEnd];
      saveSelection({multiStartIndex: startIndex, multiEndIndex: endIndex, multiSelIndex: startIndex});
    },
    [getTableModel, saveSelection]
  );

  const deleteRows = useCallback(
    (start: number | null, end: number | null): void => {
      const lastRow = end ?? start;
      if (start == null || lastRow == null) {
        return;
      }
      saveRowRangeSelection(start, lastRow);
      tableOps.deleteTableRows(getTableModel().props.table.name, start, lastRow);
    },
    [getTableModel, saveRowRangeSelection, tableOps]
  );

  const insertRowsAbove = useCallback(
    (start: number | null, end: number | null): void => {
      const lastRow = end ?? start;
      if (start == null || lastRow == null) {
        return;
      }
      saveRowRangeSelection(start, lastRow);
      tableOps.insertTableRowsAbove(getTableModel().props.table.name, start, lastRow);
    },
    [getTableModel, saveRowRangeSelection, tableOps]
  );

  const insertRowsBelow = useCallback(
    (start: number | null, end: number | null): void => {
      const lastRow = end ?? start;
      if (start == null || lastRow == null) {
        return;
      }
      saveRowRangeSelection(start, lastRow);
      tableOps.insertTableRowsBelow(getTableModel().props.table.name, start, lastRow);
    },
    [getTableModel, saveRowRangeSelection, tableOps]
  );

  const deleteTable = useCallback((): void => {
    const {deleteHandler} = getTableModel().props;
    assert(deleteHandler != null, 'Expected a delete handler when deleting the table');
    deleteHandler();
  }, [getTableModel]);

  const reorderRowsOrColumns = useCallback(
    (insertIndex: number): void => {
      const model = getTableModel();
      const {draggingDimension, multiStartIndex, multiEndIndex, multiSelIndex} = model.state;
      assert(draggingDimension != null, 'Expected to have been dragging');
      assert(multiStartIndex != null && multiEndIndex != null, 'Expected to have a selection');

      // Dragging is complete; clear the in-progress drag dimension.
      setState({draggingDimension: null});

      const {table, index, fileId, computedTable, isMaximized, isSplitScreen, updateHandler} = model.props;
      // `effectiveSlices` (start/end) is the visible column range; `boundaryIndex ?? 0` is
      // the single-input boundary used for the frozen-column computation.
      const {start: sliceStart, end: sliceEnd} = model.singleInputs.effectiveSlices;
      const siBoundary = model.singleInputs.boundaryIndex ?? 0;

      // Need to sort since start > end when you drag from the bottom to the top.
      const dimIdx = draggingDimension === TableDimension.ROW ? 0 : 1;
      const [selectionStart, selectionEnd] = [multiStartIndex[dimIdx], multiEndIndex[dimIdx]].sort(
        (a, b) => a - b
      );
      const numMoved = selectionEnd - selectionStart + 1;

      // Error if the insert index would split a partial merged cell.
      const partialMergedCellInInsertRegion = TableUtil.hasMergedCellCrossingLine(
        computedTable,
        draggingDimension,
        insertIndex,
        {
          // if the table is sliced, ignore columns that aren't shown
          ignoredColumnIndices:
            draggingDimension === TableDimension.ROW ? range(0, sliceStart - 1) : undefined,
        }
      );
      if (partialMergedCellInInsertRegion) {
        const dimensionName = draggingDimension === TableDimension.ROW ? 'row' : 'column';
        const indexToReadableName =
          draggingDimension === TableDimension.ROW ? rowIndexToNumber : colNumberToLetters;
        const mergedCellPosition = partialMergedCellInInsertRegion
          .map((idx) => indexToReadableName(idx))
          .join(' to ');
        notifications.error(
          `Cannot move ${benchlingUtil.pluralize(numMoved, dimensionName)} to position ${indexToReadableName(insertIndex)} since it crosses a merged cell in ${dimensionName}s ${mergedCellPosition}. Please unmerge the cell and try again.`,
          {id: 'drag-to-reorder-partial-merged-cell'}
        );
        return;
      }

      // Save selection to history before making the change.
      saveSelection({multiStartIndex, multiEndIndex, multiSelIndex});

      const reorderOptions: ReorderManualOptions = {
        startIndex: selectionStart,
        endIndex: selectionEnd,
        insertPosition: insertIndex,
        dim: draggingDimension,
      };

      // Update frozenColumnCount before the reorder mutation so the reorder sees the
      // correct frozen state. These are two dispatches, not a single atomic operation.
      if (draggingDimension === TableDimension.COLUMN) {
        const newFrozenCount = computeFrozenCountAfterReorder({
          selectionStart,
          selectionEnd,
          insertIndex,
          siBoundary,
          effectiveFrozen: TableUtil.getAffixedColumnCount(table),
          absoluteFreezeBoundary: TableUtil.getFreezeBoundary(table),
        });
        if (newFrozenCount != null) {
          const newTable = produce(table, (draft) => {
            draft.frozenColumnCount = newFrozenCount;
          });
          updateHandler(index, {table: newTable});
        }
      }

      // If the user dragged but didn't reorder, we can skip calling the actual reorder op.
      if (insertIndex !== selectionStart) {
        tableOps.reorderTableRowsOrColumns(table.name, reorderOptions);
      }

      // Set selection to the reordered range. `_rowRange`/`_colRange` geometry is
      // inlined here: rows span the effective slice columns, columns span all rows.
      const newFirstIndex = insertIndex > selectionStart ? insertIndex - numMoved : insertIndex;
      const newLastIndex = newFirstIndex + numMoved - 1;
      const lastRowIndex = table.table.length - 1;
      const newSelection =
        draggingDimension === TableDimension.ROW
          ? {
              start: [newFirstIndex, sliceStart] as TableIndex,
              end: [newLastIndex, sliceEnd] as TableIndex,
            }
          : {start: [0, newFirstIndex] as TableIndex, end: [lastRowIndex, newLastIndex] as TableIndex};
      setMultiIndices(newSelection);

      trackDragToReorderRowsOrColumns(
        table,
        reorderOptions,
        {fileId},
        {
          isMaximized: isMaximized ?? false,
          isFixedHeight: true,
          isSplitScreen: isSplitScreen ?? false,
        }
      );
    },
    [getTableModel, setState, saveSelection, setMultiIndices, tableOps]
  );

  const performFillDown = useCallback((): void => {
    const model = getTableModel();
    const {isLocked, lockedWarningMessage, box: fillDownBox} = model.fillDown;

    // Surface the lock warning and abort before mutating, matching the original
    // `TableEditable.isFillDownBoxLockedWithEditWarn`.
    if (isLocked) {
      if (lockedWarningMessage) {
        notifications.warn(lockedWarningMessage, {
          id: 'TableEditable.isLockedWithEditWarn',
        });
      }
      return;
    }

    const {expandedBox} = model.selection;
    // Matches the original `TableEditable._multiSelectionBox`, which hard-asserts
    // a non-null selection box in all environments.
    assert(expandedBox != null, 'Expected to have a selection');

    const {table, index, disableFormulas, computedTable, selectorsLink, updateHandler} = model.props;

    // Snapshot the current selection into undo history before mutating.
    saveSelection();

    const {
      table: newTable,
      box,
      tableOp,
      // `disableFormulas` is optional and defaults to disabled being false,
      // matching the default applied to the `TableEditable` component props.
    } = fillDownMutation(table, computedTable, expandedBox, fillDownBox, disableFormulas ?? false);

    updateHandler(index, {table: newTable}, selectorsLink.value, {
      [newTable.name]: tableOp,
    });

    setMultiIndices({
      start: [box.minI, box.minJ],
      end: [box.maxI, box.maxJ],
      text: newTable.table[box.minI][box.minJ].text,
    });
  }, [getTableModel, saveSelection, setMultiIndices]);

  const performDoubleClickFillDown = useCallback((): void => {
    const model = getTableModel();

    const {expandedBox} = model.selection;
    assert(expandedBox != null, 'Expected to have a selection');

    const {doubleClickBox} = model.fillDown;
    const {table, index, disableFormulas, computedTable, selectorsLink, updateHandler} = model.props;

    // `disableFormulas` is optional and defaults to disabled being false,
    // matching the default applied to the `TableEditable` component props.
    const result = doubleClickFillDownMutation(
      table,
      computedTable,
      expandedBox,
      doubleClickBox,
      disableFormulas ?? false
    );

    // No-op when the selection contains a merged region.
    if (result == null) {
      return;
    }

    const {table: newTable, box, tableOp} = result;

    updateHandler(index, {table: newTable}, selectorsLink.value, {
      [newTable.name]: tableOp,
    });

    setMultiIndices({
      start: [box.minI, box.minJ],
      end: [box.maxI, box.maxJ],
      text: newTable.table[box.minI][box.minJ].text,
    });
  }, [getTableModel, setMultiIndices]);

  return {
    updateCell,
    clearCellsInRange,
    clearSelection,
    deleteRows,
    insertRowsAbove,
    insertRowsBelow,
    deleteTable,
    toggleHeaderCol,
    toggleHeaderRow,
    validateMerge,
    pasteTable,
    setMultiIndices,
    saveSelection,
    reorderRowsOrColumns,
    performFillDown,
    performDoubleClickFillDown,
    renameTable: tableOps.renameTable,
    performTableOp: tableOps.performTableOp,
    deleteColumns: tableOps.deleteTableColumns,
    insertColumnsLeft: tableOps.insertTableColumnsLeft,
    insertColumnsRight: tableOps.insertTableColumnsRight,
    deleteRowsNonContiguous: tableOps.deleteTableRowsNonContiguous,
    mergeRegion: disableMergingCells ? null : tableOps.mergeTableRegion,
    unmergeRegion: disableMergingCells ? null : tableOps.unmergeTableRegion,
    reorderSingleInputs: tableOps.reorderTableRowsOrColumns,
  };
}
